import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  ChevronLeft, Euro, Loader2, CheckCircle, Clock, Landmark, 
  ArrowUpRight, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPayouts() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [payouts, setPayouts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [markingPaid, setMarkingPaid] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchPayouts();
  }, [user, token, navigate, filter]);

  const fetchPayouts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/payouts?status_filter=${filter}`, {
        headers: { 'Authorization': `Bearer ${token || localStorage.getItem('token')}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPayouts(data.payouts);
        setSummary(data.summary);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async (bookingId) => {
    setMarkingPaid(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/payouts/${bookingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ status: 'paid' }),
      });
      if (response.ok) {
        toast.success('Reversement marqué comme effectué');
        setConfirmDialog(null);
        fetchPayouts();
      } else {
        toast.error('Erreur');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setMarkingPaid(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="admin-payouts-page">
      <div className="container-app max-w-6xl">
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate('/admin')}
          data-testid="back-to-admin"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Retour au tableau de bord
        </Button>

        <h1 className="font-heading text-3xl font-semibold mb-2">
          Gestion des Reversements
        </h1>
        <p className="text-muted-foreground mb-8">
          Suivez et gérez les paiements aux prestataires
        </p>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="dashboard-card">
              <CardContent className="pt-6 text-center">
                <Euro className="w-6 h-6 mx-auto mb-2 text-accent" />
                <p className="text-2xl font-bold">{summary.total_revenue.toFixed(2)}€</p>
                <p className="text-sm text-muted-foreground">Revenus totaux</p>
              </CardContent>
            </Card>
            <Card className="dashboard-card">
              <CardContent className="pt-6 text-center">
                <ArrowUpRight className="w-6 h-6 mx-auto mb-2 text-green-500" />
                <p className="text-2xl font-bold text-green-500">{summary.total_commission.toFixed(2)}€</p>
                <p className="text-sm text-muted-foreground">Votre commission</p>
              </CardContent>
            </Card>
            <Card className="dashboard-card">
              <CardContent className="pt-6 text-center">
                <Clock className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                <p className="text-2xl font-bold text-amber-500">{summary.total_to_pay.toFixed(2)}€</p>
                <p className="text-sm text-muted-foreground">{summary.pending_count} en attente</p>
              </CardContent>
            </Card>
            <Card className="dashboard-card">
              <CardContent className="pt-6 text-center">
                <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-500" />
                <p className="text-2xl font-bold">{summary.total_paid_out.toFixed(2)}€</p>
                <p className="text-sm text-muted-foreground">{summary.paid_count} reversés</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
            data-testid="filter-all"
          >
            Tous
          </Button>
          <Button
            variant={filter === 'pending' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('pending')}
            data-testid="filter-pending"
          >
            <Clock className="w-3 h-3 mr-1" /> En attente
          </Button>
          <Button
            variant={filter === 'paid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('paid')}
            data-testid="filter-paid"
          >
            <CheckCircle className="w-3 h-3 mr-1" /> Reversés
          </Button>
        </div>

        {/* Payouts Table */}
        <Card className="dashboard-card">
          <CardContent className="pt-6">
            {payouts.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Aucun reversement à afficher</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 font-medium">Prestataire</th>
                      <th className="pb-3 font-medium">Client</th>
                      <th className="pb-3 font-medium">Date</th>
                      <th className="pb-3 font-medium text-right">Total</th>
                      <th className="pb-3 font-medium text-right">Commission</th>
                      <th className="pb-3 font-medium text-right">A reverser</th>
                      <th className="pb-3 font-medium text-center">IBAN</th>
                      <th className="pb-3 font-medium text-center">Statut</th>
                      <th className="pb-3 font-medium text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.map((payout) => (
                      <tr key={payout.booking_id} className="border-b last:border-0" data-testid={`payout-row-${payout.booking_id}`}>
                        <td className="py-3">
                          <p className="font-medium">{payout.provider_name}</p>
                          <p className="text-xs text-muted-foreground">{payout.service_category}</p>
                        </td>
                        <td className="py-3 text-muted-foreground">{payout.client_name}</td>
                        <td className="py-3 text-muted-foreground">{payout.date}</td>
                        <td className="py-3 text-right font-medium">{payout.total_price.toFixed(2)}€</td>
                        <td className="py-3 text-right text-green-500 font-medium">{payout.commission_amount.toFixed(2)}€</td>
                        <td className="py-3 text-right font-bold">{payout.provider_amount.toFixed(2)}€</td>
                        <td className="py-3 text-center">
                          {payout.has_bank_info ? (
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid={`view-bank-${payout.booking_id}`}>
                                  <Landmark className="w-3 h-3 mr-1" />
                                  Voir
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Coordonnées bancaires - {payout.provider_name}</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-3 py-4">
                                  <div>
                                    <p className="text-sm text-muted-foreground">Titulaire</p>
                                    <p className="font-medium">{payout.bank_account_holder}</p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-muted-foreground">Banque</p>
                                    <p className="font-medium">{payout.bank_name}</p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-muted-foreground">IBAN</p>
                                    <p className="font-mono font-medium">{payout.bank_iban}</p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-muted-foreground">BIC</p>
                                    <p className="font-mono font-medium">{payout.bank_bic}</p>
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                          ) : (
                            <Badge variant="outline" className="text-amber-500 border-amber-500">
                              Manquant
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 text-center">
                          {payout.payout_status === 'paid' ? (
                            <Badge className="bg-green-500">Reversé</Badge>
                          ) : (
                            <Badge variant="outline" className="text-amber-500 border-amber-500">En attente</Badge>
                          )}
                        </td>
                        <td className="py-3 text-center">
                          {payout.payout_status !== 'paid' && (
                            <Dialog open={confirmDialog === payout.booking_id} onOpenChange={(open) => setConfirmDialog(open ? payout.booking_id : null)}>
                              <DialogTrigger asChild>
                                <Button size="sm" variant="outline" data-testid={`mark-paid-${payout.booking_id}`}>
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Marquer payé
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Confirmer le reversement</DialogTitle>
                                </DialogHeader>
                                <p className="py-4 text-muted-foreground">
                                  Confirmez-vous avoir reversé <strong>{payout.provider_amount.toFixed(2)}€</strong> à <strong>{payout.provider_name}</strong> ?
                                </p>
                                <DialogFooter>
                                  <Button variant="outline" onClick={() => setConfirmDialog(null)}>Annuler</Button>
                                  <Button
                                    onClick={() => handleMarkPaid(payout.booking_id)}
                                    disabled={markingPaid}
                                    data-testid={`confirm-paid-${payout.booking_id}`}
                                  >
                                    {markingPaid ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                                    Confirmer
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
